import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Pencil, Trash2, AlertTriangle, RefreshCw, FileSpreadsheet, Download, Upload, ExternalLink } from "lucide-react";
import api from "@/lib/api";

const CATS = [
  { value: "food", label: "Продукты" },
  { value: "bath", label: "Банные принадлежности" },
  { value: "equipment", label: "Инвентарь" },
];
const EMPTY = { name: "", category: "food", unit: "порция", quantity: 0, min_quantity: 0, price_per_unit: 0, supplier: "", notes: "" };

export default function AdminInventory() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filterCat, setFilterCat] = useState("all");
  const [sheetsStatus, setSheetsStatus] = useState({ connected: false, spreadsheet_id: "" });
  const [syncing, setSyncing] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(() => {
    api.get("/admin/inventory").then((r) => setItems(r.data)).catch(() => {});
    api.get("/oauth/sheets/status").then((r) => setSheetsStatus(r.data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  // Check for sheets_connected param
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("sheets_connected") === "true") {
      window.history.replaceState({}, "", window.location.pathname);
      load();
    }
  }, [load]);

  const filtered = items.filter((i) => {
    if (filterCat !== "all" && i.category !== filterCat) return false;
    if (search && !i.name.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const lowStock = items.filter((i) => i.min_quantity > 0 && i.quantity <= i.min_quantity);

  const handleSave = async () => {
    setLoading(true);
    try {
      if (editId) await api.put(`/admin/inventory/${editId}`, form);
      else await api.post("/admin/inventory", form);
      setDialogOpen(false); setForm(EMPTY); setEditId(null); load();
    } catch { alert("Ошибка сохранения"); }
    setLoading(false);
  };

  const handleEdit = (item) => { setForm(item); setEditId(item.id); setDialogOpen(true); };
  const handleDelete = async (id) => { if (window.confirm("Удалить?")) { await api.delete(`/admin/inventory/${id}`); load(); } };

  const connectSheets = async () => {
    try {
      const r = await api.get("/oauth/sheets/login");
      window.location.href = r.data.auth_url;
    } catch (e) { alert("Ошибка подключения Google Sheets"); }
  };

  const createSheet = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/inventory/sheets/create");
      setSheetsStatus({ connected: true, spreadsheet_id: r.data.spreadsheet_id });
      alert("Таблица создана!");
    } catch (e) { alert("Ошибка: " + (e.response?.data?.detail || "unknown")); }
    setSyncing(false);
  };

  const syncToSheets = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/inventory/sheets/sync");
      alert(`Синхронизировано: ${r.data.synced_items} позиций`);
    } catch (e) { alert("Ошибка: " + (e.response?.data?.detail || "unknown")); }
    setSyncing(false);
  };

  const importFromSheets = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/admin/inventory/sheets/import");
      alert(`Импортировано: ${r.data.imported} позиций`);
      load();
    } catch (e) { alert("Ошибка: " + (e.response?.data?.detail || "unknown")); }
    setSyncing(false);
  };

  return (
    <div data-testid="admin-inventory">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h1 className="font-serif text-3xl">Бар</h1>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Google Sheets controls */}
          {!sheetsStatus.connected ? (
            <Button onClick={connectSheets} variant="outline" className="border-white/10 rounded-none gap-2 text-xs" data-testid="connect-sheets-btn">
              <FileSpreadsheet className="w-4 h-4" /> Подключить Google Sheets
            </Button>
          ) : (
            <>
              {!sheetsStatus.spreadsheet_id ? (
                <Button onClick={createSheet} disabled={syncing} variant="outline" className="border-green-600 text-green-400 rounded-none gap-2 text-xs" data-testid="create-sheet-btn">
                  <FileSpreadsheet className="w-4 h-4" /> Создать таблицу
                </Button>
              ) : (
                <>
                  <a href={`https://docs.google.com/spreadsheets/d/${sheetsStatus.spreadsheet_id}`} target="_blank" rel="noreferrer">
                    <Button variant="outline" className="border-green-600 text-green-400 rounded-none gap-1 text-xs" data-testid="open-sheet-btn">
                      <ExternalLink className="w-3 h-3" /> Таблица
                    </Button>
                  </a>
                  <Button onClick={syncToSheets} disabled={syncing} variant="outline" className="border-white/10 rounded-none gap-1 text-xs" data-testid="sync-to-sheets-btn">
                    <Upload className="w-3 h-3" /> {syncing ? "..." : "Выгрузить"}
                  </Button>
                  <Button onClick={importFromSheets} disabled={syncing} variant="outline" className="border-white/10 rounded-none gap-1 text-xs" data-testid="import-from-sheets-btn">
                    <Download className="w-3 h-3" /> {syncing ? "..." : "Загрузить"}
                  </Button>
                </>
              )}
            </>
          )}
          <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setForm(EMPTY); setEditId(null); } }}>
            <DialogTrigger asChild>
              <Button className="bg-primary text-primary-foreground rounded-none gap-2" data-testid="add-inventory-btn">
                <Plus className="w-4 h-4" /> Добавить
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-card border-white/10 max-w-lg">
              <DialogHeader><DialogTitle className="font-serif">{editId ? "Редактировать" : "Новая позиция"}</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-4">
                <Input placeholder="Название *" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-background border-white/10 rounded-none" data-testid="inv-name-input" />
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className="bg-background border-white/10 rounded-none" data-testid="inv-cat-select"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card border-white/10">
                    {CATS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Кол-во порций</label>
                    <Input type="number" value={form.quantity || ""} placeholder="0" onChange={(e) => setForm({ ...form, quantity: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" data-testid="inv-qty-input" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Мин. остаток</label>
                    <Input type="number" value={form.min_quantity || ""} placeholder="0" onChange={(e) => setForm({ ...form, min_quantity: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Цена/порция (тг)</label>
                    <Input type="number" value={form.price_per_unit || ""} placeholder="0" onChange={(e) => setForm({ ...form, price_per_unit: e.target.value === "" ? 0 : parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} className="bg-background border-white/10 rounded-none" />
                  </div>
                </div>
                <Input placeholder="Поставщик" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} className="bg-background border-white/10 rounded-none" />
                <Input placeholder="Примечание" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-background border-white/10 rounded-none" />
                <Button onClick={handleSave} disabled={loading || !form.name.trim()} className="w-full bg-primary text-primary-foreground rounded-none" data-testid="inv-save-btn">
                  {loading ? "Сохранение..." : "Сохранить"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Low stock alert */}
      {lowStock.length > 0 && (
        <div className="bg-red-900/20 border border-red-500/30 p-4 mb-6" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-red-400" />
            <span className="font-sans font-semibold text-sm text-red-400">Низкий остаток ({lowStock.length})</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {lowStock.map((i) => (
              <span key={i.id} className="text-xs bg-red-900/30 text-red-300 px-2 py-1">
                {i.name}: {i.quantity} порц. (мин. {i.min_quantity})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button onClick={() => setFilterCat("all")} className={`px-3 py-1 text-xs ${filterCat === "all" ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-white/10"}`}>
          Все ({items.length})
        </button>
        {CATS.map((c) => (
          <button key={c.value} onClick={() => setFilterCat(c.value)} className={`px-3 py-1 text-xs ${filterCat === c.value ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground border border-white/10"}`}>
            {c.label} ({items.filter((i) => i.category === c.value).length})
          </button>
        ))}
        <Input placeholder="Поиск..." value={search} onChange={(e) => setSearch(e.target.value)} className="bg-background border-white/10 rounded-none h-7 w-48 text-xs ml-auto" data-testid="inv-search" />
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div className="text-center py-12 text-muted-foreground">
          <FileSpreadsheet className="w-10 h-10 mx-auto mb-3 opacity-20" />
          <p>Бар пуст. Добавьте первую позицию.</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="inventory-table">
            <thead>
              <tr className="border-b border-white/10 text-muted-foreground text-xs">
                <th className="text-left py-2 font-normal">Название</th>
                <th className="text-left py-2 font-normal">Категория</th>
                <th className="text-right py-2 font-normal">Кол-во</th>
                <th className="text-right py-2 font-normal">Мин.</th>
                <th className="text-right py-2 font-normal">Цена/ед</th>
                <th className="text-left py-2 font-normal">Поставщик</th>
                <th className="text-right py-2 font-normal"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((i) => {
                const isLow = i.min_quantity > 0 && i.quantity <= i.min_quantity;
                return (
                  <tr key={i.id} className={`border-b border-white/5 ${isLow ? "bg-red-900/10" : ""}`} data-testid={`inv-row-${i.id}`}>
                    <td className="py-2">
                      {i.name}
                      {i.notes && <span className="text-xs text-muted-foreground ml-1">({i.notes})</span>}
                    </td>
                    <td className="py-2"><span className="text-xs text-muted-foreground">{CATS.find((c) => c.value === i.category)?.label}</span></td>
                    <td className={`py-2 text-right font-semibold ${isLow ? "text-red-400" : ""}`}>
                      {i.quantity} порц.
                      {isLow && <AlertTriangle className="w-3 h-3 text-red-400 inline ml-1" />}
                    </td>
                    <td className="py-2 text-right text-muted-foreground">{i.min_quantity}</td>
                    <td className="py-2 text-right text-primary">{i.price_per_unit > 0 ? `${i.price_per_unit.toLocaleString()} тг` : "—"}</td>
                    <td className="py-2 text-muted-foreground text-xs">{i.supplier || "—"}</td>
                    <td className="py-2 text-right">
                      <button onClick={() => handleEdit(i)} className="text-muted-foreground hover:text-foreground mr-2" data-testid={`inv-edit-${i.id}`}><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => handleDelete(i.id)} className="text-red-400 hover:text-red-300" data-testid={`inv-delete-${i.id}`}><Trash2 className="w-3 h-3" /></button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Summary */}
      <div className="mt-6 flex gap-6 text-sm text-muted-foreground">
        <span>Всего позиций: {items.length}</span>
        <span>Общая стоимость: {items.reduce((s, i) => s + i.quantity * i.price_per_unit, 0).toLocaleString()} тг</span>
      </div>
    </div>
  );
}
